/**
 * PaginationExamples.tsx
 * Comprehensive showcase of HeroUI Pagination component with trading journal context
 * 
 * Demonstrates pagination controls, navigation patterns, and customization options
 * with practical examples relevant to trading applications
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Pagination } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';

export const PaginationExamples: React.FC = () => {
  // State for interactive examples
  const [basicPage, setBasicPage] = useState(1);
  const [controlledPage, setControlledPage] = useState(5);
  const [compactPage, setCompactPage] = useState(3);
  const [loopPage, setLoopPage] = useState(1);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Pagination total={3} size="sm" isCompact />
            </div>
            <h1 className="text-3xl font-bold text-text-primary">Pagination Component</h1>
          </div>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Navigation controls for paginated content with flexible styling and behavior options.
            Perfect for data tables, search results, galleries, and any paginated interface.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Badge color="primary" variant="flat">HeroUI Based</Badge>
            <Badge color="secondary" variant="flat">Navigation</Badge>
            <Badge color="success" variant="flat">Theme Integrated</Badge>
          </div>
        </div>

        {/* Basic Pagination */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Basic Pagination</h2>
            <p className="text-text-secondary">Simple pagination with default settings</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Default Pagination</h3>
              <div className="flex flex-col items-center gap-4">
                <Pagination total={10} />
                <p className="text-sm text-text-secondary">10 pages, no controls</p>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">With Controls</h3>
              <div className="flex flex-col items-center gap-4">
                <Pagination 
                  total={10} 
                  page={basicPage}
                  onChange={setBasicPage}
                  showControls 
                />
                <p className="text-sm text-text-secondary">
                  Current page: {basicPage} of 10 • Previous/Next controls enabled
                </p>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Trading Data Example</h3>
              <div className="space-y-3">
                <div className="p-4 bg-surface rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium text-text-primary">Trade History Results</p>
                    <Chip size="sm" color="default" variant="flat">2,547 trades</Chip>
                  </div>
                  <div className="text-sm text-text-secondary space-y-1">
                    <p>Showing trades 201-250 of 2,547 total trades</p>
                    <p>50 trades per page</p>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Pagination 
                    total={51} 
                    initialPage={5}
                    showControls 
                  />
                </div>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Size Variants */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Size Variants</h2>
            <p className="text-text-secondary">Different pagination sizes for various contexts</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Small (sm)</h3>
              <div className="flex justify-center">
                <Pagination total={8} size="sm" showControls />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Medium (md) - Default</h3>
              <div className="flex justify-center">
                <Pagination total={8} size="md" showControls />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Large (lg)</h3>
              <div className="flex justify-center">
                <Pagination total={8} size="lg" showControls />
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Color Variants */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Color Themes</h2>
            <p className="text-text-secondary">Color variations for different design contexts</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-primary">Primary (Default)</h3>
                <div className="flex justify-center">
                  <Pagination total={5} color="primary" showControls />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-primary">Secondary</h3>
                <div className="flex justify-center">
                  <Pagination total={5} color="secondary" showControls />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-primary">Success</h3>
                <div className="flex justify-center">
                  <Pagination total={5} color="success" showControls />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-primary">Warning</h3>
                <div className="flex justify-center">
                  <Pagination total={5} color="warning" showControls />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-primary">Danger</h3>
                <div className="flex justify-center">
                  <Pagination total={5} color="danger" showControls />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-text-primary">Default</h3>
                <div className="flex justify-center">
                  <Pagination total={5} color="default" showControls />
                </div>
              </div>

            </div>

          </CardBody>
        </Card>

        {/* Visual Variants */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Visual Variants</h2>
            <p className="text-text-secondary">Different styling approaches for pagination</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Flat (Default)</h3>
              <div className="flex justify-center">
                <Pagination total={7} variant="flat" showControls />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Bordered</h3>
              <div className="flex justify-center">
                <Pagination total={7} variant="bordered" showControls />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Light</h3>
              <div className="flex justify-center">
                <Pagination total={7} variant="light" showControls />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Faded</h3>
              <div className="flex justify-center">
                <Pagination total={7} variant="faded" showControls />
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Advanced Features */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Advanced Features</h2>
            <p className="text-text-secondary">Special configuration options and behaviors</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Compact Mode</h3>
              <div className="flex flex-col items-center gap-4">
                <Pagination 
                  total={15} 
                  page={compactPage}
                  onChange={setCompactPage}
                  isCompact
                  showControls 
                />
                <p className="text-sm text-text-secondary">
                  Compact pagination saves space in dense layouts
                </p>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Loop Navigation</h3>
              <div className="flex flex-col items-center gap-4">
                <Pagination 
                  total={5} 
                  page={loopPage}
                  onChange={setLoopPage}
                  loop
                  showControls 
                />
                <p className="text-sm text-text-secondary">
                  Current page: {loopPage} • Loop enabled (last page → first page)
                </p>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Custom Siblings & Boundaries</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium">siblings=1, boundaries=1 (Default)</p>
                  <div className="flex justify-center">
                    <Pagination total={20} initialPage={10} showControls />
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">siblings=2, boundaries=2</p>
                  <div className="flex justify-center">
                    <Pagination total={20} initialPage={10} siblings={2} boundaries={2} showControls />
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Controlled State</h3>
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-3">
                  <Button 
                    size="sm" 
                    variant="bordered"
                    onPress={() => setControlledPage(Math.max(1, controlledPage - 1))}
                    isDisabled={controlledPage === 1}
                  >
                    Previous
                  </Button>
                  <Pagination 
                    total={10} 
                    page={controlledPage}
                    onChange={setControlledPage}
                  />
                  <Button 
                    size="sm" 
                    variant="bordered"
                    onPress={() => setControlledPage(Math.min(10, controlledPage + 1))}
                    isDisabled={controlledPage === 10}
                  >
                    Next
                  </Button>
                </div>
                <p className="text-sm text-text-secondary">
                  External controls: Page {controlledPage} of 10
                </p>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Trading Application Examples */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Trading Application Examples</h2>
            <p className="text-text-secondary">Real-world pagination scenarios for trading platforms</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Trade Journal Entries</h3>
              <div className="space-y-3">
                <div className="p-4 bg-surface rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">Journal Entries</p>
                    <Chip size="sm" color="primary" variant="flat">1,247 entries</Chip>
                  </div>
                  <p className="text-sm text-text-secondary">Viewing entries 1-20 of 1,247</p>
                </div>
                <div className="flex justify-center">
                  <Pagination total={63} color="primary" size="sm" showControls />
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Market Screener Results</h3>
              <div className="space-y-3">
                <div className="p-4 bg-surface rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">Stock Screener Results</p>
                    <Chip size="sm" color="success" variant="flat">847 matches</Chip>
                  </div>
                  <p className="text-sm text-text-secondary">Showing 1-50 of 847 stocks matching your criteria</p>
                </div>
                <div className="flex justify-center">
                  <Pagination total={17} color="success" variant="bordered" showControls />
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Portfolio Analytics</h3>
              <div className="space-y-3">
                <div className="p-4 bg-surface rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">Monthly Performance Reports</p>
                    <Chip size="sm" color="secondary" variant="flat">24 months</Chip>
                  </div>
                  <p className="text-sm text-text-secondary">Historical performance data by month</p>
                </div>
                <div className="flex justify-center">
                  <Pagination total={24} color="secondary" isCompact showControls />
                </div>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Accessibility & Best Practices */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Accessibility & Best Practices</h2>
            <p className="text-text-secondary">Guidelines for accessible and user-friendly pagination</p>
          </CardHeader>
          <CardBody className="space-y-4">
            
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Accessibility Features</h3>
              <ul className="text-text-secondary space-y-2 list-disc list-inside">
                <li>Full keyboard navigation support (arrow keys, Enter, Space)</li>
                <li>Screen reader compatible with proper ARIA labels</li>
                <li>Focus management and visual indicators</li>
                <li>Semantic HTML structure</li>
              </ul>
            </div>

            <Divider />

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Usage Guidelines</h3>
              <ul className="text-text-secondary space-y-2 list-disc list-inside">
                <li>Use <code className="px-2 py-1 bg-default-100 rounded text-sm">showControls</code> for better navigation experience</li>
                <li>Consider <code className="px-2 py-1 bg-default-100 rounded text-sm">isCompact</code> for mobile or dense layouts</li>
                <li>Provide clear context about total items and current page</li>
                <li>Use consistent pagination placement across your application</li>
                <li>Consider <code className="px-2 py-1 bg-default-100 rounded text-sm">loop</code> for circular navigation where appropriate</li>
              </ul>
            </div>

            <Divider />

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Performance Considerations</h3>
              <ul className="text-text-secondary space-y-2 list-disc list-inside">
                <li>Use controlled pagination for dynamic data loading</li>
                <li>Implement server-side pagination for large datasets</li>
                <li>Consider virtual scrolling for very large lists</li>
                <li>Cache pagination state for better user experience</li>
              </ul>
            </div>

          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};